import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2, Plus, Pencil } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { useBodyMeasurements, type BodyMeasurementRow } from "@/hooks/use-body-measurements";
import { Drawer, EmptyState, SkeletonLoader } from "@/components/system";
import { Field } from "@/routes/FinanceAccounts";

/**
 * /health/body — history list + add/edit measurement form. Replaces the
 * previous read-only weight chart for editing flow.
 */

export function BodyMeasurementHistory() {
  const q = useBodyMeasurements({ limit: 200 });
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<BodyMeasurementRow | null>(null);

  return (
    <article
      style={{
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
        padding: "var(--space-5)",
      }}
    >
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: "var(--type-body)", fontWeight: 500 }}>Body measurement history</h3>
        <button
          onClick={() => setAdding(true)}
          style={{
            background: "var(--accent)",
            color: "var(--accent-on)",
            border: "none",
            padding: "6px 12px",
            borderRadius: 8,
            fontSize: "var(--type-body-s)",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <Plus size={14} /> Add
        </button>
      </header>

      {q.isLoading ? (
        <SkeletonLoader variant="card" height={120} />
      ) : (q.data ?? []).length === 0 ? (
        <EmptyState
          headline="No measurements yet."
          sub="Tap Add to log weight, body fat, or other measurements."
        />
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 4 }}>
          {(q.data ?? []).map((m) => (
            <li
              key={m.id}
              style={{
                display: "grid",
                gridTemplateColumns: "auto 1fr auto",
                alignItems: "center",
                gap: 12,
                padding: "10px 12px",
                border: "1px solid var(--hair)",
                borderRadius: 8,
              }}
            >
              <span className="mono" style={{ fontSize: 11, color: "var(--fg-3)" }}>
                {new Date(m.ts).toLocaleDateString(undefined, { dateStyle: "medium" })}
              </span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, fontSize: "var(--type-body-s)" }}>
                {m.weight_kg != null && <span>{Number(m.weight_kg).toFixed(1)} kg</span>}
                {m.body_fat_pct != null && (
                  <span style={{ color: "var(--fg-2)" }}>BF {Number(m.body_fat_pct).toFixed(1)}%</span>
                )}
                {m.muscle_mass_kg != null && (
                  <span style={{ color: "var(--fg-2)" }}>Muscle {Number(m.muscle_mass_kg).toFixed(1)} kg</span>
                )}
                <span style={{ color: "var(--fg-3)", fontSize: 11 }}>{m.sensor_source}</span>
              </div>
              <button
                onClick={() => setEditing(m)}
                style={{
                  background: "transparent",
                  color: "var(--fg-3)",
                  border: "1px solid var(--hair-2)",
                  padding: "4px 8px",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 11,
                }}
              >
                <Pencil size={12} style={{ verticalAlign: "middle" }} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <Drawer open={adding || editing != null} onClose={() => { setAdding(false); setEditing(null); }} title={editing ? "Edit measurement" : "Add measurement"}>
        <MeasurementForm row={editing} onDone={() => { setAdding(false); setEditing(null); }} />
      </Drawer>
    </article>
  );
}

function MeasurementForm({ row, onDone }: { row: BodyMeasurementRow | null; onDone: () => void }) {
  const { session } = useSession();
  const userId = session?.user?.id;
  const queryClient = useQueryClient();
  const [ts, setTs] = useState(row ? row.ts.slice(0, 16) : new Date().toISOString().slice(0, 16));
  const [weight, setWeight] = useState(row?.weight_kg != null ? String(row.weight_kg) : "");
  const [bf, setBf] = useState(row?.body_fat_pct != null ? String(row.body_fat_pct) : "");
  const [muscle, setMuscle] = useState(row?.muscle_mass_kg != null ? String(row.muscle_mass_kg) : "");

  const save = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error("Not signed in");
      const payload = {
        user_id: userId,
        ts: new Date(ts).toISOString(),
        weight_kg: weight ? Number(weight) : null,
        body_fat_pct: bf ? Number(bf) : null,
        muscle_mass_kg: muscle ? Number(muscle) : null,
        sensor_source: "manual",
      };
      if (row) {
        const { error } = await supabase.from("body_measurement").update(payload).eq("id", row.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("body_measurement").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["body_measurements"] });
      queryClient.invalidateQueries({ queryKey: ["athlete_profile"] });
      onDone();
    },
  });

  const del = useMutation({
    mutationFn: async () => {
      if (!row) return;
      if (!confirm("Delete this measurement?")) return;
      const { error } = await supabase.from("body_measurement").delete().eq("id", row.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["body_measurements"] });
      onDone();
    },
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Field label="When">
        <input type="datetime-local" value={ts} onChange={(e) => setTs(e.target.value)} style={input} />
      </Field>
      <Field label="Weight (kg)">
        <input
          type="number"
          step="0.1"
          inputMode="decimal"
          value={weight}
          onChange={(e) => setWeight(e.target.value)}
          style={input}
        />
      </Field>
      <Field label="Body fat (%)">
        <input
          type="number"
          step="0.1"
          inputMode="decimal"
          value={bf}
          onChange={(e) => setBf(e.target.value)}
          style={input}
        />
      </Field>
      <Field label="Muscle mass (kg)">
        <input
          type="number"
          step="0.1"
          inputMode="decimal"
          value={muscle}
          onChange={(e) => setMuscle(e.target.value)}
          style={input}
        />
      </Field>
      <div style={{ display: "flex", gap: 8 }}>
        {row && (
          <button onClick={() => del.mutate()} style={{ ...ghost, color: "var(--severity-bad)" }}>
            <Trash2 size={14} style={{ verticalAlign: "middle" }} /> Delete
          </button>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={onDone} style={ghost}>Cancel</button>
        <button onClick={() => save.mutate()} disabled={save.isPending} style={primary}>
          {save.isPending ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

const input: React.CSSProperties = {
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid var(--hair-2)",
  background: "var(--bg)",
  color: "var(--fg)",
  fontSize: "var(--type-body-s)",
  fontFamily: "inherit",
  width: "100%",
  boxSizing: "border-box",
};

const primary: React.CSSProperties = {
  background: "var(--accent)",
  color: "var(--accent-on)",
  padding: "10px 16px",
  borderRadius: 10,
  border: "none",
  fontSize: "var(--type-body-s)",
  cursor: "pointer",
};

const ghost: React.CSSProperties = {
  background: "transparent",
  color: "var(--fg)",
  padding: "10px 16px",
  borderRadius: 10,
  border: "1px solid var(--hair-2)",
  fontSize: "var(--type-body-s)",
  cursor: "pointer",
};
